/**
 * Formatea una fecha ISO a un formato legible
 * @param dateString - Fecha en formato ISO (e.g., "2025-10-25T23:00:00")
 * @param includeTime - Si true, incluye la hora en el formato
 * @returns Fecha formateada (e.g., "25/10/2025" o "25/10/2025 23:00")
 */
export function formatDate(dateString: string | undefined | null, includeTime: boolean = false): string {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);

    // Verificar si la fecha es válida
    if (isNaN(date.getTime())) {
      return dateString;
    }

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    const dateFormatted = `${day}/${month}/${year}`;

    if (includeTime) {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${dateFormatted} ${hours}:${minutes}`;
    }

    return dateFormatted;
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
}

/**
 * Formatea una fecha ISO a formato relativo (hoy, ayer, mañana, etc.)
 * @param dateString - Fecha en formato ISO
 * @returns Fecha en formato relativo o fecha formateada
 */
export function formatDateRelative(dateString: string | undefined | null, language: string = 'es'): string {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);

    const diffTime = dateOnly.getTime() - today.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    const translations = {
      es: {
        today: 'Hoy',
        yesterday: 'Ayer',
        tomorrow: 'Mañana'
      },
      en: {
        today: 'Today',
        yesterday: 'Yesterday',
        tomorrow: 'Tomorrow'
      }
    };

    const t = translations[language as keyof typeof translations] || translations.es;

    if (diffDays === 0) return t.today;
    if (diffDays === -1) return t.yesterday;
    if (diffDays === 1) return t.tomorrow;

    return formatDate(dateString);
  } catch (error) {
    console.error('Error formatting relative date:', error);
    return dateString;
  }
}
